import { forwardRef, useImperativeHandle, useState } from 'react';

import classNames from 'classnames';
import Header from 'components/Header';
import InputField from 'components/ui/InputField';
import MobileModal from 'components/ui/Modal/MobileModal';
import DesktopLayout from 'layouts/DesktopLayout';
import MobileLayout from 'layouts/MobileLayout';
import i18n from 'providers/Locale';

import styles from './List.module.scss';

export type HorizontalListOptionType = {
	label: string;
	value: string;
	labelJSX?: JSX.Element;
	labelInHead?: string | JSX.Element;
	disabled?: boolean;
	hidden?: boolean;
};

export type HorizontalListOptionRef = {
	cleanSearch: () => void;
};

type ListProps = {
	ref?: React.LegacyRef<HorizontalListOptionRef>;
	position?: 'top' | 'bottom' | 'right' | 'left';
	searchable?: boolean;
	options: HorizontalListOptionType[];
	label?: string | JSX.Element;
	centerOptions?: boolean;
	name: string;
	value?: string;
	isOpen: boolean;
	onChangeOpenState: (state: boolean) => void;
	onChange: (option: string, name: string) => void;
};

export const List: React.FC<ListProps> = forwardRef(
	(
		{
			position,
			searchable,
			options,
			label,
			value,
			name,
			isOpen,
			centerOptions,
			onChangeOpenState,
			onChange,
		},
		ref
	) => {
		const [searchValue, setSearchValue] = useState<string>('');

		const onChangeSearch = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
			setSearchValue(target.value);
		};

		const onOptionClick = (event: React.MouseEvent, option: HorizontalListOptionType) => {
			if (option.disabled) return event.stopPropagation();
			onChange(option.value, name);
		};

		useImperativeHandle(ref, () => ({
			cleanSearch: setSearchValue.bind(null, ''),
		}));

		return (
			<>
				<DesktopLayout>
					<div
						className={classNames([
							'custom-select__options',
							styles.root,
							position === 'top' && styles.isTop,
							position === 'right' && styles.isRight,
							isOpen && styles.isOpened,
						])}>
						{searchable && (
							<div className={styles.searchable} onClick={(e) => e.stopPropagation()}>
								<input
									autoFocus
									type="text"
									value={searchValue}
									onChange={onChangeSearch}
									placeholder={i18n('search')}
								/>
							</div>
						)}
						<Options
							options={options}
							searchValue={searchValue}
							searchable={searchable}
							value={value}
							onSelect={onOptionClick}
						/>
					</div>
				</DesktopLayout>
				<MobileLayout>
					<MobileModal visible={isOpen} onClose={onChangeOpenState.bind(null, false)} title="">
						<div className={classNames(styles.root, centerOptions && styles.centerOptions)}>
							<Header title={label} collapsed onGoBack={onChangeOpenState.bind(null, false)} />
							<div className={styles.body}>
								{searchable && (
									<div className={styles.searchable} onClick={(e) => e.stopPropagation()}>
										<InputField
											size={'large'}
											autoFocus
											type="text"
											value={searchValue}
											onChange={onChangeSearch}
											placeholder={i18n('search')}
										/>
									</div>
								)}
								<Options
									options={options}
									searchValue={searchValue}
									searchable={searchable}
									value={value}
									onSelect={onOptionClick}
								/>
							</div>
						</div>
					</MobileModal>
				</MobileLayout>
			</>
		);
	}
);

type OptionsProps = {
	searchable?: boolean;
	options: HorizontalListOptionType[];
	searchValue: string;
	value?: string;
	onSelect: (event: React.MouseEvent, option: HorizontalListOptionType) => void;
};

const Options: React.FC<OptionsProps> = ({ options, searchable, searchValue, value, onSelect }) => {
	const searchableFilter = (option: HorizontalListOptionType): boolean => {
		const lowerSearch = searchValue.toLowerCase();

		if (!searchable) return true;
		if (option.label?.toLowerCase().includes(lowerSearch)) return true;
		if (option.value?.toLowerCase().includes(lowerSearch)) return true;

		return false;
	};

	return (
		<>
			{options.filter(searchableFilter).map((option, i) => (
				<div
					className={classNames(styles.item, {
						[styles.isSelected]: option.value === value,
						[styles.isDisabled]: option.disabled,
					})}
					key={`${option.label} + ${option.value} + ${i}`}
					onClick={(event) => onSelect(event, option)}>
					{option.labelJSX || option.label}
				</div>
			))}
		</>
	);
};
